<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    @input="$emit('input', $event)"
  >
    <v-card
      tile
      class="d-flex flex-column rsv-wizard-card"
    >
      <v-card-title class="text-h5 font-weight-bold">
        予約を追加
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <wizard-stepper
        class="rsv-wizard-stepper"
        :current-step="step"
        :step-names="['日時・人数', '指名・コース', '確認']"
        :next-disabled="!canGoNext"
        @navigate="goToStep"
      />

      <v-card-text class="rsv-wizard-body flex-grow-1 pt-4 px-6">
        <div class="rsv-wizard-content">
          <!-- Step1: 日時・人数 -->
          <div v-if="step === 1">
            <v-row dense>
              <v-col
                cols="6"
                md="3"
              >
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      :value="form.reservedDate"
                      label="日付"
                      readonly
                      outlined
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    :value="form.reservedDate"
                    locale="ja"
                    :day-format="date => new Date(date).getDate()"
                    @input="form.reservedDate = $event; dateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col
                cols="6"
                md="3"
              >
                <v-text-field
                  v-model.number="form.customerCount"
                  label="人数"
                  type="number"
                  min="1"
                  outlined
                  hide-details
                />
              </v-col>
              <v-col
                cols="6"
                md="3"
                class="mt-md-0 mt-2"
              >
                <div class="drum-label">
                  開始時間
                </div>
                <time-option-drum-picker
                  v-model="form.startTime"
                  :options="timeOptions"
                />
              </v-col>
              <v-col
                cols="6"
                md="3"
                class="mt-md-0 mt-2"
              >
                <div class="drum-label">
                  終了予定（任意・目安）
                </div>
                <time-option-drum-picker
                  v-model="form.endTime"
                  :options="[{ text: '未設定', value: null }, ...timeOptions]"
                />
              </v-col>
            </v-row>
            <div class="wizard-muted text-caption mt-1">
              5分単位・スクロールで選択できます
            </div>
            <div class="wizard-muted text-caption mt-3">
              卓は来店時に選びます（卓作成ウィザード）。終了予定は空でもOK
            </div>
          </div>

          <!-- Step2: 指名・コース -->
          <div v-else-if="step === 2">
            <div class="wz-sec-label">
              指名<template v-if="form.nominations.length > 0">
                （{{ form.nominations.length }}件 設定済み）
              </template>
            </div>
            <div
              v-if="form.nominations.length > 0"
              class="d-flex flex-wrap align-center wz-chips mb-2"
            >
              <v-chip
                v-for="(row, i) in form.nominations"
                :key="`nom-${row.userId}-${row.nominationId}`"
                color="primary"
                close
                @click:close="form.nominations.splice(i, 1)"
              >
                <b class="me-1">{{ castNameOf(row.userId) }}</b>・{{ nominationNameOf(row.nominationId) }}
              </v-chip>
              <v-btn
                v-if="!showNominationInput"
                small
                text
                color="primary"
                @click="showNominationInput = true"
              >
                ＋ 指名を追加
              </v-btn>
            </div>
            <v-row
              v-if="form.nominations.length === 0 || showNominationInput"
              dense
            >
              <v-col cols="5">
                <v-select
                  v-model="nominationDraft.userId"
                  :items="castOptions"
                  label="キャスト"
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="5">
                <v-select
                  v-model="nominationDraft.nominationId"
                  :items="nominationOptions"
                  label="指名種別"
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col
                cols="2"
                class="d-flex align-center"
              >
                <v-btn
                  small
                  outlined
                  block
                  :disabled="!nominationDraft.userId || !nominationDraft.nominationId"
                  @click="addNomination"
                >
                  追加
                </v-btn>
              </v-col>
            </v-row>

            <div class="wz-sec-label">
              コース
            </div>
            <v-row dense>
              <v-col cols="8">
                <v-select
                  v-model="form.courseId"
                  :items="courseOptions"
                  :label="courseRequired ? 'コース（必須）' : 'コース'"
                  :error="courseMissing"
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
            </v-row>
            <div
              v-if="courseMissing"
              class="wz-course-hint mt-2"
            >
              <v-icon
                size="16"
                color="error"
              >
                {{ icons.mdiAlertCircleOutline }}
              </v-icon>
              指名がある場合はコースが必須です
            </div>
            <div
              v-else
              class="wizard-muted text-caption mt-3"
            >
              指名なしの予約はこのまま「次へ」で進めます
            </div>
          </div>

          <!-- Step3: 確認（＋任意項目） -->
          <div v-else>
            <v-row dense>
              <v-col
                cols="12"
                md="6"
              >
                <div class="wz-confirm mb-4">
                  <div class="wz-confirm__row">
                    <span class="wz-confirm__key">日時</span>
                    <span><b>{{ confirmDateTime }}</b></span>
                    <button
                      type="button"
                      class="wz-confirm__edit"
                      @click="step = 1"
                    >
                      修正
                    </button>
                  </div>
                  <div class="wz-confirm__row">
                    <span class="wz-confirm__key">人数</span>
                    <span><b>{{ form.customerCount || 1 }}名</b></span>
                    <button
                      type="button"
                      class="wz-confirm__edit"
                      @click="step = 1"
                    >
                      修正
                    </button>
                  </div>
                  <div class="wz-confirm__row">
                    <span class="wz-confirm__key">指名</span>
                    <span>{{ confirmNominations }}</span>
                    <button
                      type="button"
                      class="wz-confirm__edit"
                      @click="step = 2"
                    >
                      修正
                    </button>
                  </div>
                  <div class="wz-confirm__row">
                    <span class="wz-confirm__key">コース</span>
                    <span>{{ confirmCourse }}</span>
                    <button
                      type="button"
                      class="wz-confirm__edit"
                      @click="step = 2"
                    >
                      修正
                    </button>
                  </div>
                </div>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <button
                  type="button"
                  class="detail-toggle"
                  @click="isDetailOpen = !isDetailOpen"
                >
                  詳細（任意）
                  <span class="detail-toggle__count">{{ detailSummary }}</span>
                  <v-icon
                    size="18"
                    class="detail-toggle__caret"
                    :class="{ 'detail-toggle__caret--open': isDetailOpen }"
                  >
                    {{ icons.mdiChevronDown }}
                  </v-icon>
                </button>
                <v-expand-transition>
                  <div v-show="isDetailOpen">
                    <v-row dense>
                      <v-col cols="6">
                        <v-select
                          v-model="form.referralId"
                          :items="referralOptions"
                          label="紹介者"
                          outlined
                          hide-details
                          clearable
                        />
                      </v-col>
                    </v-row>
                    <v-select
                      v-model="form.customerTagIds"
                      :items="customerTagOptions"
                      label="顧客タグ"
                      class="mt-3"
                      outlined
                      hide-details
                      multiple
                      chips
                      small-chips
                      deletable-chips
                    />
                    <div class="text-subtitle-2 mt-4 mb-2">
                      顧客
                    </div>
                    <customer-preview
                      v-for="customer in form.customers"
                      :key="customer.id"
                      :customer="customer"
                      @remove="removeCustomer(customer.id)"
                    />
                    <v-btn
                      small
                      outlined
                      class="mb-2"
                      @click="customerSelectOpen = true"
                    >
                      <v-icon
                        small
                        class="mr-1"
                      >
                        {{ icons.mdiPlus }}
                      </v-icon>
                      顧客を追加
                    </v-btn>
                    <v-textarea
                      v-model="form.memo"
                      label="メモ"
                      class="mt-3"
                      outlined
                      hide-details
                      rows="2"
                      auto-grow
                    />
                  </div>
                </v-expand-transition>
              </v-col>
            </v-row>

            <customer-select-dialog
              v-model="customerSelectOpen"
              :selected-ids="form.customers.map(c => c.id)"
              @select="addCustomer"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="rsv-wizard-footer px-6 py-3">
        <v-btn
          text
          :disabled="step === 1 || isSaving"
          @click="step -= 1"
        >
          戻る
        </v-btn>
        <span class="wizard-muted ms-2">{{ selectionSummary }}</span>
        <v-spacer />
        <v-btn
          v-if="step < 3"
          color="primary"
          x-large
          :disabled="!canGoNext"
          @click="goNext"
        >
          次へ
        </v-btn>
        <v-btn
          v-else
          color="primary"
          x-large
          :disabled="courseMissing"
          :loading="isSaving"
          @click="submit"
        >
          予約を保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiClose, mdiPlus, mdiChevronDown, mdiAlertCircleOutline,
} from '@mdi/js'
import ReservationApi from '@/api/waiter/Reservation'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import {
  buildReservationParams,
  commitNominationDraft,
} from '@/views/waiter/reservations/reservationPayload'
import { buildTimeOptions } from '@/utils/timeOptions'
import TimeOptionDrumPicker from '@/views/components/time/TimeOptionDrumPicker.vue'
import WizardStepper from '@/views/components/table/TableCreateWizard/WizardStepper.vue'
import CustomerPreview from './CustomerPreview.vue'
import CustomerSelectDialog from './CustomerSelectDialog.vue'

// 予約の新規登録ウィザード（日時・人数 → 指名・コース → 確認）。
// 仮予約は廃止済みのため、保存は常に「確定」1本（.steering/20260909-remove-tentative-status/）
export default {
  name: 'ReservationCreateWizard',
  components: {
    TimeOptionDrumPicker,
    WizardStepper,
    CustomerPreview,
    CustomerSelectDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    defaultDate: {
      type: String,
      default: null,
    },
    casts: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    // 指名種別（Nomination vendibles の生データ）
    nominations: {
      type: Array,
      default: () => [],
    },
    referrals: {
      type: Array,
      default: () => [],
    },
    customerTags: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      icons: {
        mdiClose, mdiPlus, mdiChevronDown, mdiAlertCircleOutline,
      },
      step: 1,
      form: this.getInitialForm(),
      nominationDraft: { userId: null, nominationId: null },
      // 指名の追加入力行の表示（設定済みがある場合は「＋指名を追加」で開く）
      showNominationInput: false,
      dateMenu: false,
      customerSelectOpen: false,
      isDetailOpen: false,
      isSaving: false,
      // 予約日の出勤予定キャスト（選択肢の並び順に使用）
      scheduledCastIds: [],
    }
  },
  computed: {
    timeOptions() {
      return buildTimeOptions()
    },
    castOptions() {
      // 予約日の出勤予定者を先頭に並べる（（出勤）表記付き）
      const scheduled = new Set(this.scheduledCastIds.map(Number))
      const options = this.casts.map(c => {
        const id = parseInt(c.id, 10)
        const name = c.attributes?.name || c.name
        const isScheduled = scheduled.has(id)

        return {
          text: isScheduled ? `${name}（出勤）` : name,
          value: id,
          isScheduled,
        }
      })

      return [
        ...options.filter(o => o.isScheduled),
        ...options.filter(o => !o.isScheduled),
      ]
    },
    nominationOptions() {
      return this.nominations.map(n => ({
        text: n.attributes?.name || n.name,
        value: parseInt(n.id, 10),
      }))
    },
    courseOptions() {
      return this.courses.map(c => ({
        text: c.attributes?.name || c.name,
        value: parseInt(c.id, 10),
      }))
    },
    referralOptions() {
      return this.referrals.map(r => ({
        text: r.attributes?.name || r.name,
        value: parseInt(r.id, 10),
      }))
    },
    customerTagOptions() {
      return this.customerTags.map(t => ({
        text: t.attributes?.name || t.name,
        value: parseInt(t.id, 10),
      }))
    },
    courseRequired() {
      return this.form.nominations.length > 0
    },
    courseMissing() {
      return this.courseRequired && !this.form.courseId
    },
    canGoNext() {
      if (this.step === 1) return !!this.form.reservedDate && !!this.form.startTime
      if (this.step === 2) return !this.courseMissing

      return true
    },
    selectionSummary() {
      const parts = []
      if (this.form.reservedDate && this.form.startTime) {
        parts.push(`${this.form.reservedDate.slice(5).replace('-', '/')} ${this.form.startTime}〜`)
      }
      parts.push(`${this.form.customerCount || 1}名`)
      if (this.step >= 2 && this.form.nominations.length > 0) {
        parts.push(`指名${this.form.nominations.length}件`)
      }

      return parts.join(' / ')
    },
    confirmDateTime() {
      const end = this.form.endTime ? `〜${this.form.endTime}` : '〜'
      const suffix = this.form.endTime ? '' : '（終了未設定）'

      return `${this.form.reservedDate} ${this.form.startTime}${end}${suffix}`
    },
    confirmNominations() {
      if (this.form.nominations.length === 0) return 'なし'

      return this.form.nominations
        .map(n => `${this.castNameOf(n.userId)}（${this.nominationNameOf(n.nominationId)}）`)
        .join('・')
    },
    confirmCourse() {
      if (!this.form.courseId) return 'なし'
      const course = this.courseOptions.find(c => c.value === this.form.courseId)

      return course?.text || 'なし'
    },
    // 詳細セクションの入力済みサマリー（「入力済み 2件: タグ・メモ」）
    detailFilledLabels() {
      const labels = []
      if (this.form.referralId) labels.push('紹介者')
      if (this.form.customerTagIds.length > 0) labels.push('タグ')
      if (this.form.customers.length > 0) labels.push('顧客')
      if ((this.form.memo || '').trim() !== '') labels.push('メモ')

      return labels
    },
    detailSummary() {
      if (this.detailFilledLabels.length === 0) return '（紹介者・タグ・顧客・メモ）'

      return `（入力済み ${this.detailFilledLabels.length}件: ${this.detailFilledLabels.join('・')}）`
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.step = 1
        this.form = this.getInitialForm()
        this.nominationDraft = { userId: null, nominationId: null }
        this.showNominationInput = false
        this.isDetailOpen = false
        this.fetchScheduledCasts()
      }
    },
    // 日付が変わったら出勤予定者（キャスト並び順）も追従させる
    'form.reservedDate': function onReservedDateChange() {
      this.fetchScheduledCasts()
    },
  },
  methods: {
    getInitialForm() {
      return {
        reservedDate: this.defaultDate || new Date().toISOString().slice(0, 10),
        startTime: '20:00',
        endTime: null,
        customerCount: 1,
        hallId: null,
        tableNumber: null,
        nominations: [],
        courseId: null,
        referralId: null,
        customerTagIds: [],
        customers: [],
        memo: '',
      }
    },
    async fetchScheduledCasts() {
      if (!this.form.reservedDate) {
        this.scheduledCastIds = []

        return
      }
      try {
        const res = await WorkScheduleApi.getWorkSchedules({
          status: 'approved',
          requestType: 'work',
          startDate: this.form.reservedDate,
          endDate: this.form.reservedDate,
        })
        this.scheduledCastIds = (res.data?.work_schedules?.data || []).map(d => Number(d.attributes.userId))
      } catch (error) {
        // 並び順の付加情報のため、取得失敗時は通常順のまま
        this.scheduledCastIds = []
      }
    },
    // ステッパーからのジャンプ。戻りは自由、進みは1stepずつ検証しながら
    goToStep(target) {
      if (target < this.step) {
        this.step = target

        return
      }
      while (this.step < target) {
        const before = this.step
        this.goNext()
        if (this.step === before) break
      }
    },
    goNext() {
      // 指名の追加行が入力途中のままなら確定してから進める（追加ボタンの押し忘れ対策）
      if (this.step === 2 && !this.commitDraft()) return
      if (this.step === 2 && this.courseMissing) return
      this.step += 1
    },
    commitDraft() {
      const result = commitNominationDraft(this.nominationDraft, this.form.nominations)
      if (!result.ok) {
        this.$emit('error', result.error)

        return false
      }
      this.form.nominations = result.nominations
      this.nominationDraft = { userId: null, nominationId: null }

      return true
    },
    addNomination() {
      const { userId, nominationId } = this.nominationDraft
      if (!userId || !nominationId) return
      const exists = this.form.nominations.find(n => n.userId === userId && n.nominationId === nominationId)
      if (exists) {
        this.$emit('error', '同じ指名が既に追加されています')

        return
      }
      this.form.nominations.push({ userId, nominationId })
      this.nominationDraft = { userId: null, nominationId: null }
      this.showNominationInput = false
    },
    castNameOf(userId) {
      const cast = this.casts.find(c => parseInt(c.id, 10) === Number(userId))

      return cast?.attributes?.name || cast?.name || `ID:${userId}`
    },
    nominationNameOf(nominationId) {
      const nomination = this.nominations.find(n => parseInt(n.id, 10) === Number(nominationId))

      return nomination?.attributes?.name || nomination?.name || `ID:${nominationId}`
    },
    addCustomer(customer) {
      this.form.customers.push(customer)
    },
    removeCustomer(id) {
      this.form.customers = this.form.customers.filter(c => c.id !== id)
    },
    async submit() {
      if (!this.commitDraft()) return
      if (this.courseMissing) {
        this.$emit('error', '指名がある予約はコースの選択が必要です（来店処理のセット計上に使われます）')

        return
      }

      this.isSaving = true
      try {
        const params = { ...buildReservationParams(this.form, { isEdit: false }), status: 'confirmed' }
        await ReservationApi.createReservation(params)
        this.$emit('saved')
        this.close()
      } catch (e) {
        const msg = e.response?.data?.errors?.join(', ') || '保存に失敗しました'
        this.$emit('error', msg)
      } finally {
        this.isSaving = false
      }
    },
    close() {
      this.$emit('input', false)
    },
  },
}
</script>

<style scoped>
/* 全画面ウィザード: 卓作成ウィザード(TableCreateWizard)と同じく
   カードをviewport高さに固定し、本文だけスクロール・フッターは画面下部に固定 */
.rsv-wizard-card {
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
}

.rsv-wizard-body {
  overflow-y: auto;
}

/* タブレット全画面で間延びしないよう本文は中央寄せで幅を絞る */
.rsv-wizard-content {
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
  /* タブレットで文字が細く見えるため本文の基本ウェイトを上げる */
  font-weight: 500;
}

.rsv-wizard-footer {
  flex: 0 0 auto;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.wizard-muted {
  font-size: 16px;
  color: #94a3b8;
  font-weight: 500;
}

/* ステッパー(卓作成ウィザードと同じ番号付きヘッダー) */
.rsv-wizard-stepper {
  flex: 0 0 auto;
}

.rsv-wizard-stepper ::v-deep .v-stepper__header {
  box-shadow: none;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  max-width: 1080px;
  margin: 0 auto;
}

.rsv-wizard-stepper ::v-deep .v-stepper__label {
  font-size: 18px;
  font-weight: 700;
}

.rsv-wizard-stepper ::v-deep .v-stepper__step__step {
  width: 36px;
  height: 36px;
  min-width: 36px;
  font-size: 17px;
}

.rsv-wizard-stepper ::v-deep .v-stepper__step {
  padding: 20px 24px;
}

/* セクション見出し（右側に区切り線を伸ばす） */
.wz-sec-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #94a3b8;
  margin: 18px 0 12px;
}

.wz-sec-label:first-child {
  margin-top: 0;
}

.wz-sec-label::after {
  content: '';
  flex: 1;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

/* 指名あり×コース未選択の警告 */
.wz-course-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.35);
  border-radius: 6px;
  padding: 6px 10px;
}

.wz-chips {
  gap: 6px;
}

/* 確認サマリー */
.wz-confirm {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  overflow: hidden;
}

.wz-confirm__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 17px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.wz-confirm__row:last-child {
  border-bottom: none;
}

.wz-confirm__key {
  width: 96px;
  flex-shrink: 0;
  color: #94a3b8;
  font-weight: 500;
}

.wz-confirm__edit {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #9155fd;
  font-size: 15.5px;
  cursor: pointer;
  font-family: inherit;
}

/* 詳細（任意項目）の開閉トグル */
.detail-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(145, 85, 253, 0.06);
  border-radius: 6px;
  padding: 16px 20px;
  font-size: 16.5px;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
  margin: 0 0 12px;
  text-align: left;
}

.detail-toggle__count {
  font-size: 15px;
  font-weight: 400;
  color: #94a3b8;
}

.detail-toggle__caret {
  margin-left: auto !important;
  transition: transform 0.15s;
}

.detail-toggle__caret--open {
  transform: rotate(180deg);
}

.drum-label {
  font-size: 13.5px;
  color: #94a3b8;
  font-weight: 500;
  margin-bottom: 4px;
}

/* タブレット向け: Vuetifyのtext-caption(12px)をこの画面では読みやすいサイズに上書き */
.text-caption {
  font-size: 13px !important;
  font-weight: 500 !important;
}
</style>
